import React, { useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import moment from 'moment';
import toast from 'react-hot-toast';
import cx from 'classnames';
import { FileText, Trash2 } from 'lucide-react';
import {
  useFetchMessageQuery,
  useDeleteMessageMutation,
} from '@@/services/message';
import { useFetchPostQuery } from '@@/services/post';
import { useFetchPropertyQuery } from '@@/services/property';
import { translateResidentType } from '@@/constants/user';

const MessageDocumentPreview: React.FC<{
  type?: string;
  thumbnail?: string;
  img?: string;
  filename?: string;
  originalFilename?: string;
}> = ({ type, thumbnail, img, filename, originalFilename }) => {
  switch (type) {
    case 'IMAGE':
      if (thumbnail) {
        if (img) {
          return (
            <Link
              to={img}
              className="mt-1"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={thumbnail} width={80} />
            </Link>
          );
        }

        return <img src={thumbnail} width={80} className="mt-1" />;
      }
      break;

    case 'DOCUMENT':
      if (filename) {
        return (
          <Link
            to={`https://static.alacaza.fr/${encodeURI(originalFilename!)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 text-body-sm mt-2"
          >
            <FileText size={20} />
            <span>{filename}</span>
          </Link>
        );
      }
      break;

    default:
      break;
  }

  return <p className="text-body-sm text-neutral-500">Aucune</p>;
};

const MessagePage = () => {
  const navigation = useNavigate();
  const { messageId } = useParams();
  const [
    deleteMessage,
    { isSuccess: deleteIsSuccess, isError: deleteIsError },
  ] = useDeleteMessageMutation();

  const { data: message } = useFetchMessageQuery(messageId || '', {
    skip: !messageId,
  });
  const { data: property } = useFetchPropertyQuery(message?.propertyId || '', {
    skip: !message?.propertyId,
  });
  const { data: post } = useFetchPostQuery(message?.postId || '', {
    skip: !message?.postId,
  });

  const onDeleteMessage = useCallback(() => {
    if (messageId) {
      const confirmation = message?.sentAt
        ? 'Ce message a déjà été envoyé. Il sera retiré pour tous les résidents, mais les notifications déjà reçues ne peuvent pas être annulées. Confirmez-vous la suppression ?'
        : 'Etes-vous sûr de vouloir supprimer ce message ?';
      if (window.confirm(confirmation)) {
        deleteMessage(messageId);
      }
    }
  }, [messageId, message?.sentAt, deleteMessage]);

  useEffect(() => {
    if (deleteIsSuccess) {
      navigation(-1);
      toast.success('Le message a bien été supprimé', {
        position: 'top-right',
      });
    }
  }, [deleteIsSuccess, navigation]);

  useEffect(() => {
    if (deleteIsError) {
      toast.error(
        "Le message n'a pas pu être supprimé. Merci de contacter le support Alacaza.",
        {
          position: 'top-right',
        },
      );
    }
  }, [deleteIsError]);

  const rightPanel = useMemo(() => {
    if (!message) {
      return null;
    }

    if (message.postId) {
      if (!post) {
        return null;
      }

      return (
        <div className="space-y-6">
          <div>
            <h3 className="text-heading-sm text-neutral-900 mb-4">
              📊 Sondage
            </h3>
            <hr className="border-neutral-200 mb-4" />

            <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">
              Titre
            </p>
            <p className="text-body text-neutral-900 mb-4">{post.title}</p>

            <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">
              Description
            </p>
            <p className="text-body text-neutral-900 mb-4 break-words">
              {post.body}
            </p>

            <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-3">
              Réponses
            </p>

            <div className="space-y-4">
              {post.pollOptions?.map((opt: Alacaza.PostPollOption) => {
                const votePower = post.nbVoters
                  ? Math.round(((opt.nbVotes || 0) / post.nbVoters) * 100)
                  : 0;
                return (
                  <div
                    key={opt.ref}
                    className="bg-neutral-50 border border-neutral-200 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="inline-flex items-center px-3 py-1.5 bg-warning-400 text-neutral-900 rounded-badge text-body-sm font-semibold">
                        {opt.label}
                      </span>
                      <span className="text-body-sm text-neutral-600">
                        {opt.nbVotes} vote(s) ({votePower}%)
                      </span>
                    </div>
                    {!!opt.votes?.length && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {opt.votes.map((vote) => (
                          <Link
                            key={vote.userId}
                            to={`/users/${vote.userId}`}
                            className="text-primary-600 hover:text-primary-700 text-body-xs underline"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {vote.userName}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {!!post.images?.length && (
              <div className="mt-6">
                <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-3">
                  Images
                </p>
                <div className="flex flex-wrap gap-3">
                  {post.images?.map((image: Alacaza.PostImage, i: number) => (
                    <Link
                      key={i}
                      to={`https://static.alacaza.fr/${encodeURI(
                        image.largeFilename,
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="border border-neutral-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                    >
                      <img
                        alt={image.largeFilename}
                        src={image.large}
                        width={80}
                      />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div>
          <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">
            Message
          </p>
          <p className="text-body text-neutral-900 break-words">
            {message.message}
          </p>
        </div>

        <hr className="border-neutral-200" />

        <div>
          <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">
            Pièce(s) jointe(s)
          </p>
          <MessageDocumentPreview {...message.discussionDocument} />
        </div>
      </div>
    );
  }, [message, post]);

  const createdAt = useMemo(
    () => moment(message?.createdAt),
    [message?.createdAt],
  );
  const scheduledAt = useMemo(
    () => moment(message?.scheduledAt),
    [message?.scheduledAt],
  );
  const sentAt = useMemo(() => moment(message?.sentAt), [message?.sentAt]);

  if (!message || !property) {
    return null;
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="px-page-sm py-6 lg:px-page lg:py-8 w-full">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-card shadow-card border border-neutral-200 p-6 mb-6">
            <div className="inline-flex items-center px-3 py-1.5 bg-warning-400 text-neutral-900 rounded-badge text-body font-semibold mb-4">
              Message #{messageId}
            </div>
            <p className="text-body-sm text-neutral-600">
              Message créé le {createdAt.format('LL')} à{' '}
              {createdAt.format('LT')}
              &nbsp;par&nbsp;
              <span className="font-medium text-neutral-900">
                {message.createdByUserName}
              </span>
            </p>
          </div>

          {/* Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Panel */}
            <div className="bg-white rounded-card shadow-card border border-neutral-200 p-6 space-y-6">
              <div>
                <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">
                  Résidence
                </p>
                <p className="text-body text-neutral-900">
                  {message.propertyName}
                </p>
              </div>

              {!!message.filters && (
                <div>
                  <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-3">
                    Filtre(s)
                  </p>
                  <div className="space-y-3">
                    {!!message.filters?.buildingIds?.length && (
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-body-sm text-neutral-700">
                          Bâtiment(s):
                        </p>
                        {message.filters?.buildingIds.map((id: string) => {
                          const buildingName = property.buildings.find(
                            (b) => b.buildingId === id,
                          )?.name;

                          return (
                            <span
                              key={id}
                              className={cx(
                                'inline-flex items-center px-3 py-1 rounded-badge text-body-xs font-medium',
                                buildingName
                                  ? 'bg-warning-400 text-neutral-900'
                                  : 'bg-danger-500 text-white',
                              )}
                            >
                              {buildingName || id}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {!!message.filters?.floors?.length && (
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-body-sm text-neutral-700">
                          Étage(s):
                        </p>
                        {message.filters?.floors.map((floor: string) => (
                          <span
                            key={floor}
                            className="inline-flex items-center px-3 py-1 rounded-badge text-body-xs font-medium bg-warning-400 text-neutral-900"
                          >
                            {floor}
                          </span>
                        ))}
                      </div>
                    )}
                    {!!message.filters?.residentTypes?.length && (
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-body-sm text-neutral-700">
                          Typologie des résidents:
                        </p>
                        {message.filters?.residentTypes
                          .map(translateResidentType)
                          .map((type: string) => (
                            <span
                              key={type}
                              className="inline-flex items-center px-3 py-1 rounded-badge text-body-xs font-medium bg-warning-400 text-neutral-900"
                            >
                              {type}
                            </span>
                          ))}
                      </div>
                    )}
                    {!!message.filters?.isMemberOfCondominiumCouncil && (
                      <div className="flex items-center gap-2">
                        <p className="text-body-sm text-neutral-700">
                          Membres du conseil syndical uniquement:
                        </p>
                        <span className="inline-flex items-center px-3 py-1 rounded-badge text-body-xs font-medium bg-warning-400 text-neutral-900">
                          OUI
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div>
                <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">
                  Groupe de discussion
                </p>
                <p className="text-body text-neutral-900">
                  {message.roomTitle}
                </p>
              </div>

              <div>
                <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">
                  Envoi programmé le
                </p>
                <p className="text-body text-neutral-900">
                  {scheduledAt.format('LL')} à {scheduledAt.format('LT')}
                </p>
              </div>

              <div>
                <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">
                  Envoyé le
                </p>
                <p className="text-body text-neutral-900">
                  {message.sentAt
                    ? `${sentAt.format('LL')} à ${sentAt.format('LT')}`
                    : '-'}
                </p>
              </div>
            </div>

            {/* Right Panel */}
            <div className="bg-white rounded-card shadow-card border border-neutral-200 p-6">
              {rightPanel}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end mt-6">
            <button
              className="inline-flex items-center gap-2 px-6 py-3 bg-danger-600 hover:bg-danger-700 text-white font-medium rounded-button shadow-button transition-colors duration-200"
              onClick={onDeleteMessage}
            >
              <Trash2 size={18} />
              Supprimer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagePage;
